/**
 * Specimen helpers shared by the diagnostic test screens.
 *
 * A diagnostic test can be classified two independent ways:
 *  - `category` / `diagnosticCategory` — the routing discriminator (LAB, RADIOLOGY, ...).
 *  - its linked specimen container — what is physically collected, if anything.
 *
 * Containers are diagnostic *items*: the single accessory link flagged
 * `isSpecimenContainer = true` is the source of truth for the sample type. The
 * container's `specimenType` drives the blood/sample classification:
 *   EDTA → BLOOD, PLAIN → SERUM, FLUORIDE/CITRATE/HEPARIN → PLASMA,
 *   URINE_POT → URINE, STOOL_POT → STOOL, SWAB_TUBE → SWAB, SLIDE → TISSUE.
 *
 * These codes are seeded defaults — the catalogue is admin-editable, so keep this
 * list small and treat everything else as "other".
 */
export const BLOOD_SPECIMEN_TYPE_CODES = ['BLOOD', 'SERUM', 'PLASMA'];

export interface SpecimenContainerItem {
  code?: string | null;
  name?: string | null;
  color?: string | null;
  isContainer?: boolean | null;
  specimenType?: { code?: string | null; name?: string | null } | null;
}

export interface SpecimenAwareTest {
  accessories?: Array<{
    isSpecimenContainer?: boolean | null;
    requiredVolume?: string | null;
    accessory?: SpecimenContainerItem | null;
  }> | null;
}

/** The container accessory linked to a test as its specimen container, if any. */
export function containerForTest(test?: SpecimenAwareTest | null): SpecimenContainerItem | null {
  const link = test?.accessories?.find((item) => item.isSpecimenContainer);
  return link?.accessory || null;
}

/** The sample type code for a test (from its container item). */
export function testSpecimenTypeCode(test?: SpecimenAwareTest | null): string | null {
  return containerForTest(test)?.specimenType?.code || null;
}

/** Human-readable sample type name for a test. */
export function testSpecimenTypeName(test?: SpecimenAwareTest | null): string | null {
  return containerForTest(test)?.specimenType?.name || null;
}

/** True when the test collects blood (blood / serum / plasma family). */
export function isBloodTest(test?: SpecimenAwareTest | null): boolean {
  const code = testSpecimenTypeCode(test);
  return !!code && BLOOD_SPECIMEN_TYPE_CODES.includes(code);
}

/** True when the test collects a physical specimen at all (i.e. has a container). */
export function requiresSpecimen(test?: SpecimenAwareTest | null): boolean {
  return !!containerForTest(test);
}

/** Optional per-test override of the container's default volume. */
export function requiredVolumeForTest(test?: SpecimenAwareTest | null): string | null {
  const link = test?.accessories?.find((item) => item.isSpecimenContainer);
  return link?.requiredVolume || null;
}

/** Display label for the container, e.g. "EDTA (Purple top) · Purple". */
export function testContainerLabel(test?: SpecimenAwareTest | null): string {
  const container = containerForTest(test);
  if (!container) return '—';
  return [container.name || container.code, container.color].filter(Boolean).join(' · ');
}

/**
 * Minimal shape of a specimen (and the tests on it) needed to resolve the
 * diagnostic categories it belongs to. Category is a property of the tests, so
 * it is read from each linked test's dynamic category (falling back to the test
 * type enum). A specimen can carry tests from more than one category, so the
 * distinct names are returned joined by ", ".
 */
export interface SpecimenCategorySource {
  items?: Array<{
    receiptItem?: {
      test?: {
        diagnosticCategory?: { name?: string | null } | null;
        category?: string | null;
      } | null;
    } | null;
  }> | null;
}

/** Distinct diagnostic category names carried by a specimen's tests. */
export function specimenCategoryNames(
  specimen?: SpecimenCategorySource | null,
): string {
  const names = new Set<string>();
  for (const item of specimen?.items || []) {
    const test = item?.receiptItem?.test;
    const name = test?.diagnosticCategory?.name || test?.category;
    if (name) names.add(String(name));
  }
  return Array.from(names).join(', ');
}

/** A diagnostic receipt item (accessories carry no category). */
export interface ReceiptItemCategorySource {
  isAccessory?: boolean | null;
  test?: {
    diagnosticCategory?: { name?: string | null } | null;
    category?: string | null;
  } | null;
}

/** Distinct diagnostic category names across a list of receipt items. */
export function receiptItemsCategoryNames(
  items?: Array<ReceiptItemCategorySource | null> | null,
): string {
  const names = new Set<string>();
  for (const item of items || []) {
    if (!item || item.isAccessory) continue;
    const name = item.test?.diagnosticCategory?.name || item.test?.category;
    if (name) names.add(String(name));
  }
  return Array.from(names).join(', ');
}
